// Routed case detail (cases/:id). Coo-lab-style two-column layout adapted to Alignova's data:
// tabbed content (Overview / Files / Grading / Timeline) on the left, a sticky Workflow sidebar
// (status transitions + staff assignment) and status history on the right. Loads the case + its
// event history once, then keeps everything fresh off the case.updated SSE.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  type Case,
  type CaseEvent,
  type CaseStatus,
  type CaseGradeDto,
  CASE_STATUS_LABELS,
  CASE_TYPE_LABELS,
  CASE_PRIORITY_LABELS,
  CASE_ARCH_LABELS,
  CASE_RELATION_LABELS,
  CASE_GRADE_LABELS,
  CASE_CLASSIFICATION_LABELS,
  formatDate,
  formatDateTime,
  formatMoney,
} from '@alignova/shared';
import { api } from '../../lib/api.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { t } from '../../i18n/index.ts';
import { Card, Pill } from '../../ui/primitives.tsx';
import { CaseFilesPanel } from './CaseFilesPanel.tsx';
import { CaseWorkflowPanel } from './CaseWorkflowPanel.tsx';
import { CaseComments } from './CaseComments.tsx';
import { CaseChat } from './CaseChat.tsx';
import { CaseRequests } from './CaseRequests.tsx';
import { CaseChecklist } from './CaseChecklist.tsx';
import { CaseTreatmentPlans } from './CaseTreatmentPlans.tsx';

interface CaseDetail {
  case: Case;
  events: CaseEvent[];
}

type Tab =
  | 'overview'
  | 'files'
  | 'checklist'
  | 'plans'
  | 'comments'
  | 'chat'
  | 'requests'
  | 'grading'
  | 'timeline';
const TABS: Tab[] = [
  'overview',
  'files',
  'checklist',
  'plans',
  'comments',
  'chat',
  'requests',
  'grading',
  'timeline',
];

/** Humanized elapsed time between an ISO instant and now (days/hours). */
function elapsed(fromIso: string): string {
  const ms = Date.now() - new Date(fromIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '-';
  const mins = Math.floor(ms / 60000);
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins % 60}m`;
  return `${mins}m`;
}

function statusTone(status: CaseStatus): 'neutral' | 'good' | 'bad' {
  if (status === 'delivered') return 'good';
  if (status === 'cancelled' || status === 'remake') return 'bad';
  return 'neutral';
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="border-b border-line py-2">
      <dt className="text-xs uppercase tracking-wide text-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-ink">{children == null || children === '' ? t('cases.detail.none') : children}</dd>
    </div>
  );
}

function HistoryList({ events }: { events: CaseEvent[] }) {
  if (events.length === 0) return <p className="py-4 text-center text-sm text-muted">{t('cases.timeline.empty')}</p>;
  return (
    <ol className="space-y-3">
      {events.map((ev) => (
        <li key={ev.id} className="flex gap-3">
          <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />
          <div className="min-w-0">
            <p className="text-sm text-ink">
              {ev.fromStatus
                ? `${CASE_STATUS_LABELS[ev.fromStatus]} → ${CASE_STATUS_LABELS[ev.toStatus]}`
                : t('cases.timeline.created')}
            </p>
            {ev.note && <p className="text-xs text-muted">{ev.note}</p>}
            <p className="font-mono text-xs text-muted">{formatDateTime(ev.at)}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function GradingTab({ caseId }: { caseId: string }) {
  const [grade, setGrade] = useState<CaseGradeDto | null>(null);
  const [state, setState] = useState<'loading' | 'none' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    // The endpoint returns 200 with a null body when the case has no grade yet (not a 404).
    api
      .get<CaseGradeDto | null>(`/grading/${caseId}`)
      .then((g) => {
        if (cancelled) return;
        if (!g) {
          setState('none');
          return;
        }
        setGrade(g);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  if (state === 'loading') return <p className="text-sm text-muted">{t('common.loading')}</p>;
  if (state === 'error') return <p className="text-sm text-status-urgent-fg">{t('cases.detail.loadError')}</p>;
  if (state === 'none' || !grade) {
    return (
      <div>
        <p className="mb-3 text-sm text-muted">{t('cases.detail.notGraded')}</p>
        <a href="/grading" className="text-sm text-accent hover:underline">
          {t('cases.detail.viewGrading')}
        </a>
      </div>
    );
  }
  return (
    <div>
      <dl className="grid gap-x-8 sm:grid-cols-2">
        <Field label={t('cases.grade.grade')}>{CASE_GRADE_LABELS[grade.grade]}</Field>
        <Field label={t('cases.grade.classification')}>
          {grade.classification ? CASE_CLASSIFICATION_LABELS[grade.classification] : null}
        </Field>
        <Field label={t('cases.grade.shade')}>{grade.shade}</Field>
        <Field label={t('cases.grade.status')}>
          <Pill tone={grade.status === 'approved' ? 'good' : 'neutral'}>
            {t(grade.status === 'approved' ? 'cases.grade.status.approved' : 'cases.grade.status.draft')}
          </Pill>
        </Field>
        <Field label={t('cases.grade.lockedPrice')}>
          {grade.lockedUnitPrice != null ? formatMoney(grade.lockedUnitPrice, grade.currency) : null}
        </Field>
      </dl>
      {grade.findings && (
        <div className="mt-3">
          <p className="text-xs uppercase tracking-wide text-muted">{t('cases.grade.findings')}</p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-ink">{grade.findings}</p>
        </div>
      )}
      <a href="/grading" className="mt-4 inline-block text-sm text-accent hover:underline">
        {t('cases.detail.viewGrading')}
      </a>
    </div>
  );
}

export function CaseDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<CaseDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');

  const load = useCallback(() => {
    api
      .get<CaseDetail>(`/cases/${id}`)
      .then(setData)
      .catch(() => setError(t('cases.detail.loadError')));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Any case moved (our own action here, a teammate's edit, a replayed offline write) — refetch.
  useLiveEvent('case.updated', () => load());

  if (error) {
    return (
      <div>
        <button onClick={() => navigate('/cases')} className="text-sm text-accent hover:underline">
          {t('cases.detail.back')}
        </button>
        <p className="mt-4 text-sm text-status-urgent-fg">{error}</p>
      </div>
    );
  }
  if (!data) return <p className="text-muted">{t('common.loading')}</p>;

  const c = data.case;
  const isOrtho = c.caseType === 'aligner' || c.caseType === 'retainer';
  const events = [...data.events].sort((a, b) => (a.at < b.at ? 1 : -1)); // newest first

  return (
    <div>
      <button onClick={() => navigate('/cases')} className="text-sm text-accent hover:underline">
        {t('cases.detail.back')}
      </button>

      <header className="mt-3 mb-5 flex flex-wrap items-center gap-3">
        <h1 className="font-mono text-xl font-semibold tracking-tight text-ink">{c.caseNumber}</h1>
        <Pill tone={statusTone(c.status)}>{CASE_STATUS_LABELS[c.status]}</Pill>
        <Pill tone="neutral">{CASE_TYPE_LABELS[c.caseType]}</Pill>
        <Pill tone={c.priority === 'normal' ? 'neutral' : 'bad'}>{CASE_PRIORITY_LABELS[c.priority]}</Pill>
        <span className="text-sm text-muted">{c.clientName}</span>
      </header>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Left: tabbed content */}
        <div className="lg:col-span-2">
          <div className="mb-4 flex gap-2 border-b border-line">
            {TABS.map((tb) => (
              <button
                key={tb}
                onClick={() => setTab(tb)}
                className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
                  tab === tb ? 'border-primary text-ink' : 'border-transparent text-muted hover:text-ink'
                }`}
              >
                {t(`cases.tab.${tb}` as 'cases.tab.overview')}
              </button>
            ))}
          </div>

          <Card className="p-5">
            {tab === 'overview' && (
              <>
                <dl className="grid gap-x-8 sm:grid-cols-2">
                  <Field label={t('cases.detail.field.type')}>{CASE_TYPE_LABELS[c.caseType]}</Field>
                  <Field label={t('cases.detail.field.service')}>{c.service}</Field>
                  <Field label={t('cases.detail.field.client')}>{c.clientName}</Field>
                  <Field label={t('cases.detail.field.patientCode')}>{c.patientCode}</Field>
                  {isOrtho ? (
                    <Field label={t('cases.detail.field.arch')}>{c.arch ? CASE_ARCH_LABELS[c.arch] : null}</Field>
                  ) : (
                    <Field label={t('cases.detail.field.tooth')}>{c.tooth}</Field>
                  )}
                  {c.caseType === 'aligner' && (
                    <>
                      <Field label={t('cases.detail.field.stages')}>
                        {`${c.totalStagesUpper ?? '-'} / ${c.totalStagesLower ?? '-'}`}
                      </Field>
                      <Field label={t('cases.detail.field.material')}>{c.alignerMaterial}</Field>
                    </>
                  )}
                  <Field label={t('cases.detail.field.priority')}>{CASE_PRIORITY_LABELS[c.priority]}</Field>
                  <Field label={t('cases.detail.field.status')}>{CASE_STATUS_LABELS[c.status]}</Field>
                  <Field label={t('cases.detail.field.due')}>{formatDate(c.dueDate)}</Field>
                  <Field label={t('cases.detail.field.received')}>{formatDateTime(c.receivedAt)}</Field>
                  <Field label={t('cases.detail.field.turnaround')}>{elapsed(c.receivedAt)}</Field>
                  {(c.punchingType || c.punchingInstructions) && (
                    <Field label={t('cases.detail.field.punching')}>
                      {[c.punchingType, c.punchingInstructions].filter(Boolean).join(' - ')}
                    </Field>
                  )}
                  {c.relationType && (
                    <Field label={t('cases.detail.field.relation')}>
                      {`${CASE_RELATION_LABELS[c.relationType]}${c.refinementRound ? ` · #${c.refinementRound}` : ''}`}
                    </Field>
                  )}
                  {c.setupApprovedAt && (
                    <Field label={t('cases.detail.field.setupApproved')}>{formatDateTime(c.setupApprovedAt)}</Field>
                  )}
                </dl>
                {c.notes && (
                  <div className="mt-4">
                    <p className="text-xs uppercase tracking-wide text-muted">{t('cases.detail.field.notes')}</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-ink">{c.notes}</p>
                  </div>
                )}
              </>
            )}
            {tab === 'files' && <CaseFilesPanel caseId={c.id} />}
            {tab === 'checklist' && <CaseChecklist caseValue={c} onSaved={load} />}
            {tab === 'plans' && <CaseTreatmentPlans caseId={c.id} />}
            {tab === 'comments' && <CaseComments caseId={c.id} />}
            {tab === 'chat' && <CaseChat caseId={c.id} />}
            {tab === 'requests' && <CaseRequests caseId={c.id} />}
            {tab === 'grading' && <GradingTab caseId={c.id} />}
            {tab === 'timeline' && <HistoryList events={events} />}
          </Card>
        </div>

        {/* Right: sticky workflow sidebar */}
        <div className="space-y-5 lg:sticky lg:top-4 lg:self-start">
          <CaseWorkflowPanel c={c} onChanged={load} onDeleted={() => navigate('/cases')} />
          <Card className="p-4">
            <h3 className="mb-3 text-sm font-semibold text-ink">{t('cases.detail.history')}</h3>
            <HistoryList events={events} />
          </Card>
        </div>
      </div>
    </div>
  );
}
